import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { formatMoney } from "@/utils/generalUtilities";
import CalculatorPng from "@/assets/calculator.png";
import PiggyBankPng from "@/assets/piggy-bank.png";
import Tooltip from "@/components/Common/Tooltip";

interface GoalSummaryProps {
  purpose: string;
  year: number;
  projectedAmount?: number;
  monthlyContribution?: number;
}

const GoalSummary: React.FC<GoalSummaryProps> = ({
  purpose,
  year,
  projectedAmount,
  monthlyContribution,
}) => {
  const [originalMonthlyContribution, setOriginalMonthlyContribution] =
    useState<number>(0);

  const { t } = useTranslation();

  useEffect(() => {
    if (monthlyContribution) {
      setOriginalMonthlyContribution(monthlyContribution);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!purpose) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
    >
      <div className="flex flex-col space-y-[12px]">
        <div className="w-full flex items-center space-x-[12px]">
          <img src={CalculatorPng} alt="Calculator" className="w-[18px]" />
          <span className="text-[16px] leading-[127%] font-normal">
            {t("onboarding.goals.summary.projected_cost", {
              year,
              educationType: t(
                `onboarding.goals.summary.${
                  purpose === "CollegeFund" ? "college" : "trade_school"
                }`,
              ),
              amount: projectedAmount ? formatMoney(projectedAmount) : "",
            })}{" "}
            <span className="text-[18px] ml-1">
              <Tooltip
                text={t("onboarding.goals.summary.projected_cost_tooltip")}
              />
            </span>
          </span>
        </div>
        <div className="w-full flex items-center space-x-[12px]">
          <img src={PiggyBankPng} alt="Piggy Bank" className="w-[20px]" />
          <p className="text-[16px] leading-[127%] font-normal">
            {t("onboarding.goals.summary.monthly_contribution", {
              goalType: t(
                `onboarding.goals.summary.${
                  monthlyContribution !== originalMonthlyContribution
                    ? "set"
                    : "estimated"
                }`,
              ),
              amount: monthlyContribution
                ? formatMoney(monthlyContribution)
                : 0,
            })}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default GoalSummary;
